import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import Tag from 'components/generic/tag';
import Countdown from 'components/countdown/Countdown';
import usePageContext from 'hooks/user/usePageContext';
import { em } from 'modules/helpers/style';
import { breakpoint, sizes } from 'variables';
import type { ISplash } from './shared/types';
import { handleColor } from './shared/helpers';

const StyledTag = styled(Tag)<{
    $isTransparent: ISplash['isTransparant'];
    $alternativeStyles: boolean;
}>`
    & > div {
        ${breakpoint.up(sizes.sm)} {
            display: flex;
            flex-direction: column;
        }
    }

    ${({ $isTransparent }) =>
        $isTransparent &&
        css`
            padding-left: 0;
            padding-right: 0;
        `}

    ${({ $alternativeStyles }) =>
        $alternativeStyles &&
        css`
            border-radius: 0;
        `}

    ${({ theme }) => theme.TagSplash}
`;

const Text = styled.div<{
    $isTransparent: ISplash['isTransparant'];
    $tagSize?: ISplash['size'];
}>`
    ${breakpoint.up(sizes.sm)} {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
    }

    ${({ $isTransparent, $tagSize }) =>
        ($isTransparent || $tagSize === 'xl') &&
        css`
            display: flex;
            flex-wrap: wrap;
        `}
`;

const StyledCountdown = styled(Countdown)`
    display: block;
    font-weight: inherit;
    text-transform: none;
    align-items: flex-start;
    width: unset;
`;

const SeparatorBullet = styled.span`
    display: inline-block;
    margin-left: ${em(6)};
    margin-right: ${em(6)};
    width: ${em(4)};
    height: ${em(4)};
    border-radius: 100%;
    background-color: currentColor;
    vertical-align: middle;
    margin-top: ${em(-2)};
`;

function notLast(index: number, array: any[]) {
    return index < array.length - 1;
}

export default function TagSplash(splash: ISplash): JSX.Element {
    const {
        isTransparant: isTransparent,
        lines,
        size,
        type,
        backgroundColor,
        textColor,
        endDate,
        discountId,
        ...restProps
    } = splash;
    const formattedBackgroundColor = useMemo(() => handleColor(backgroundColor), [backgroundColor]);
    const formattedTextColor = useMemo(() => handleColor(textColor), [textColor]);
    const pageContext = usePageContext();

    return (
        <StyledTag
            backgroundColor={formattedBackgroundColor}
            textColor={formattedTextColor}
            $isTransparent={isTransparent}
            size={size}
            $alternativeStyles={pageContext?.brandContext === 'chanel' || false}
            {...restProps}
            data-discountid={discountId} // For BE debugging
        >
            <Text $isTransparent={isTransparent} $tagSize={size}>
                {lines.map((line, index) => (
                    <React.Fragment key={index}>
                        {line}
                        {notLast(index, lines) && <SeparatorBullet />}
                    </React.Fragment>
                ))}
            </Text>

            {endDate && (
                <StyledCountdown countdown={{ finishDateTime: endDate, sizeType: 'XS', showTimeUnits: true }} />
            )}
        </StyledTag>
    );
}
